import type { ReactNode } from 'react'
import { AdminCard } from './AdminCard'
import { useState, useEffect, useCallback } from 'react'
import { db } from '@core/db/db'
import { fiscalQueueWorker } from '@core/services/fiscal/FiscalQueueWorker'
import type { KktStatus, SelfTestCheck } from '@core/services/fiscal/FiscalService'
import type { OfflineQueueItem } from '@core/db/models'
import type { AdminSectionProps } from './types'
import { ScalesStatusCard } from './ScalesStatusCard'
import { money } from '@core/utils/format'
import { humanizeFiscalError } from '@core/utils/httpError'
import { btn, btnBase, btnSize, cx, hint, table, tableWrap, td, tdNum, th, thNum } from '@ui/classes'
import { eqBtn, eqRow, eqSmallBtn, errBanner, okBanner } from '@ui/admin'
import { Alert, Check, Dot, Minus, Phone } from '@ui/icons'

/** Человеческие подписи статусов очереди фискализации. */
const QUEUE_STATUS: Record<string, string> = {
  pending: 'Ожидает отправки',
  sent: 'Отправлен',
  failed: 'Ошибка'
}

function queueStatusLabel(status: string): string {
  return QUEUE_STATUS[status] ?? status
}

/** Тональность бейджа статуса очереди (текст — тёмный для AA). */
const QUEUE_TONE: Record<string, string> = {
  pending: 'bg-amber-100 text-warnFg',
  sent: 'bg-green-100 text-okDark',
  failed: 'bg-red-50 text-dangerDark'
}

function kktStateLabel(state: KktStatus['state']): string {
  switch (state) {
    case 'ok':
      return 'ок'
    case 'paper_out':
      return 'нет бумаги'
    case 'shift_closed':
      return 'смена закрыта'
    case 'error':
      return 'ошибка'
    case 'no_device':
      return 'нет связи'
  }
}

/** Человеческая подпись пункта самопроверки. */
function selfTestLabel(status: SelfTestCheck['status']): string {
  switch (status) {
    case 'ok':
      return 'OK'
    case 'warn':
      return 'Внимание'
    case 'fail':
      return 'Ошибка'
    case 'skip':
      return 'Пропущено'
  }
}

/** Класс индикатора состояния ККТ. */
function kktStateTone(state: KktStatus['state']): string {
  if (state === 'ok') return 'text-okDark'
  if (state === 'shift_closed') return 'text-amber-700'
  return 'text-danger'
}

/** Иконка пункта самопроверки. */

function selfTestIcon(status: SelfTestCheck['status']): ReactNode {
  switch (status) {
    case 'ok':
      return <Check size={16} className="text-okDark" />
    case 'warn':
      return <Alert size={16} className="text-amber-700" />
    case 'fail':
      return <Alert size={16} className="text-danger" />
    case 'skip':
      return <Minus size={16} className="text-muted" />
  }
}

/**
 * Раздел «Оборудование»: диагностика ККТ (статус, ФН, ОФД, ошибки,
 * пошаговая самопроверка) и очередь дофискализации с точечным повтором.
 *
 * Статус виден всем; действия (тест-печать, повторы) — только при праве
 * can_manage_shifts (передаётся как canManage).
 */

export function EquipmentSection({ core, perms, onPairDevice, devicePaired }: AdminSectionProps) {
  const canManage = perms.canManage
  const fiscal = core.fiscal
  const [status, setStatus] = useState<KktStatus | null>(() => fiscal.getKktStatus())
  const [checks, setChecks] = useState<SelfTestCheck[] | null>(null)
  const [queue, setQueue] = useState<OfflineQueueItem[]>([])
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  // Push-статус ККТ: приходит при подключении и при изменении состояния.
  useEffect(() => {
    const off = fiscal.onKktStatus((next) => setStatus(next))
    fiscal.requestStatus()
    return () => off()
  }, [fiscal])

  /** Перечитать очередь дофискализации. */
  const refreshQueue = useCallback(async () => {
    const items = await db.offlineQueue.where('type').equals('fiscalize').toArray()
    items.sort((a, b) => b.createdAt - a.createdAt)
    setQueue(items)
  }, [])

  // Очередь обновляется после действий и по таймеру (10 с).
  useEffect(() => {
    void refreshQueue()
    const timer = window.setInterval(() => void refreshQueue(), 10000)
    return () => clearInterval(timer)
  }, [refreshQueue])

  const runSelfTest = async () => {
    setBusy(true)
    setError(null)
    try {
      setChecks(await fiscal.runSelfTest())
      setMessage('Самопроверка выполнена')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const refreshStatus = () => {
    setError(null)
    fiscal.requestStatus()
    setMessage('Запрошен актуальный статус ККТ')
  }

  const doTestPrint = async () => {
    if (!window.confirm('Тест-печать израсходует бумагу. Продолжить?')) return
    setBusy(true)
    setError(null)
    try {
      const result = await fiscal.testPrint()
      if (result.success) setMessage('Тестовая печать выполнена')
      else setError(result.error ?? 'Тест-печать не удалась')
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const retryOne = async (id?: number) => {
    if (id == null) return
    setBusy(true)
    setError(null)
    try {
      const ok = await fiscalQueueWorker.processItem(id)
      if (ok) setMessage('Чек пробит на ККТ')
      else setError('Не удалось пробить чек на ККТ — см. последнюю ошибку в таблице')
      await refreshQueue()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const retryAll = async () => {
    setBusy(true)
    setError(null)
    try {
      const count = await fiscalQueueWorker.processPending()
      setMessage(`Пробито отложенных чеков: ${count}`)
      await refreshQueue()
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : String(err))
    } finally {
      setBusy(false)
    }
  }

  const pendingCount = queue.filter((i) => i.status === 'pending').length
  const daysLeft = status?.fn?.daysLeft
  const fnColor =
    daysLeft == null
      ? 'text-ink'
      : daysLeft < 0
        ? 'text-danger'
        : daysLeft < 30
          ? 'text-amber-700'
          : 'text-okDark'
  const ofdUnsent = status?.ofd?.unsentDocs ?? 0
  const ofdColor = ofdUnsent > 100 ? 'text-danger' : ofdUnsent > 0 ? 'text-amber-700' : 'text-okDark'

  return (
    <>
      <AdminCard
        id="equipment.kkt"
        title="Фискальный регистратор"
        defaultOpen
        meta={
          <span className="text-small font-normal text-muted">
            {status ? kktStateLabel(status.state) : 'нет данных'}
          </span>
        }
      >
        {message && <p className={okBanner}>{message}</p>}
        {error && <p className={errBanner}>{error}</p>}

        {!status && (
          <p className={hint}>Не удаётся получить состояние кассы — проверьте соединение</p>
        )}

        {status && (
          <>
            <p className="mb-2.5 mt-1">
              Состояние:{' '}
              <strong className={kktStateTone(status.state)}>
                <Dot
                  size={16}
                  className={status.state === 'ok' ? 'text-okDark' : status.state === 'shift_closed' ? 'text-amber-500' : 'text-danger'}
                />{' '}
                {kktStateLabel(status.state)}
              </strong>
            </p>

            <div className={tableWrap}>
              <table className={table}>
                <tbody>
                  <tr>
                    <td className={td}>Модель</td>
                    <td className={td}>{status.model ?? '—'}</td>
                    <td className={td}>Серийный номер</td>
                    <td className={td}>{status.serial ?? '—'}</td>
                  </tr>
                  <tr>
                    <td className={td}>Прошивка</td>
                    <td className={td}>{status.firmware ?? '—'}</td>
                    <td className={td}>Интерфейс</td>
                    <td className={td}>{status.interface ?? '—'}</td>
                  </tr>
                  <tr>
                    <td className={td}>РНМ</td>
                    <td className={td}>{status.rnm ?? '—'}</td>
                    <td className={td}>ИНН</td>
                    <td className={td}>{status.inn ?? '—'}</td>
                  </tr>
                  <tr>
                    <td className={td}>СНО</td>
                    <td className={td}>{status.sno ?? '—'}</td>
                    <td className={td}>Смена</td>
                    <td className={td}>{status.shiftOpen ? 'открыта' : 'закрыта'}</td>
                  </tr>
                  <tr>
                    <td className={td}>Бумага</td>
                    <td className={td}>
                      {status.paper === 'ok'
                        ? 'есть'
                        : status.paper === 'low'
                          ? 'заканчивается'
                          : status.paper === 'out'
                            ? 'нет'
                            : '—'}
                    </td>
                    <td className={td}>Крышка</td>
                    <td className={td}>{status.coverOpen ? 'открыта' : 'закрыта'}</td>
                  </tr>
                  <tr>
                    <td className={td}>Последний документ</td>
                    <td className={td}>{status.lastDocNumber ?? '—'}</td>
                    <td className={td}>Часы ККТ</td>
                    <td className={td}>
                      {status.clock ? new Date(status.clock).toLocaleString('ru-RU') : '—'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div className="mt-3 flex flex-wrap gap-4">
              <div className="min-w-0 flex-1 basis-[260px]">
                <strong>Фискальный накопитель</strong>
                <p className={eqRow}>
                  Номер: <code>{status.fn?.number ?? '—'}</code>
                </p>
                <p className={eqRow}>
                  Действует до:{' '}
                  {status.fn?.validTo
                    ? new Date(status.fn.validTo).toLocaleDateString('ru-RU')
                    : '—'}
                </p>
                <p className={cx(eqRow, 'font-semibold', fnColor)}>
                  Осталось дней: {daysLeft ?? '—'}
                  {daysLeft != null && daysLeft < 30 && daysLeft >= 0 && (
                    <span className="ml-1 inline-flex items-center gap-1 text-amber-700"><Alert size={16} /> скоро замена</span>
                  )}
                  {daysLeft != null && daysLeft < 0 && (
                    <span className="ml-1 inline-flex items-center gap-1 text-danger"><Alert size={16} /> срок истёк</span>
                  )}
                </p>
                <p className={eqRow}>Состояние ФН: {status.fn?.state ?? '—'}</p>
              </div>

              <div className="min-w-0 flex-1 basis-[260px]">
                <strong>ОФД</strong>
                <p className={cx(eqRow, 'font-semibold', ofdColor)}>
                  Документов ждут подтверждения: {ofdUnsent}
                </p>
                <p className={eqRow}>
                  Последняя квитанция:{' '}
                  {status.ofd?.lastSentAt
                    ? new Date(status.ofd.lastSentAt).toLocaleString('ru-RU')
                    : '—'}
                </p>
                <p className={eqRow}>Ошибка ОФД: {status.ofd?.lastError ?? 'нет'}</p>
                <p className={hint}>
                  Очередью ОФД управляет ФН — из кассы её можно только читать.
                </p>
              </div>
            </div>

            <div className="mt-3">
              <strong>Ошибки устройства</strong>
              {status.errors.length === 0 ? (
                <p className={okBanner}>ошибок нет</p>
              ) : (
                <ul className="mt-1.5 pl-[18px]">
                  {status.errors.map((e) => (
                    <li key={e.code} className={e.fatal ? 'text-danger' : 'text-amber-700'}>
                      <code>{e.code}</code> — {e.text}
                      {e.fatal ? ' (фатальная)' : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mt-3 flex flex-wrap gap-2">
              <button className={eqBtn} onClick={refreshStatus}>
                Проверить
              </button>
              <button
                className={eqBtn}
                onClick={() => void doTestPrint()}
                disabled={busy || !canManage}
                title={
                  canManage ? 'Нефискальная тест-печать' : 'Требуется право управления сменами'
                }
              >
                Печать теста
              </button>
              <button className={eqBtn} onClick={() => void runSelfTest()} disabled={busy}>
                Самопроверка
              </button>
            </div>
            {!canManage && (
              <p className={hint}>
                Действия с оборудованием доступны администратору (право управления сменами).
              </p>
            )}
          </>
        )}
      </AdminCard>

      {/* Чек-лист самопроверки ККТ */}
      <AdminCard
        id="equipment.selftest"
        title="Самопроверка ККТ"
        meta={
          checks ? (
            <span className="text-small font-normal text-muted">
              ошибок: {checks.filter((check) => check.status === 'fail').length}
            </span>
          ) : undefined
        }
      >
        {!checks && <p className={hint}>Нажмите «Повторить», чтобы выполнить проверку.</p>}
        {checks && (
          <div className={tableWrap}>
            <table className={table}>
              <thead>
                <tr>
                  <th className={th}>Проверка</th>
                  <th className={th}>Статус</th>
                  <th className={th}>Детали</th>
                </tr>
              </thead>
              <tbody>
                {checks.map((check) => (
                  <tr key={check.id}>
                    <td className={td}>{check.label}</td>
                    <td className={td}>
                      {selfTestIcon(check.status)} {selfTestLabel(check.status)}
                    </td>
                    <td className={td}>{check.detail ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-2.5">
          <button className={eqBtn} onClick={() => void runSelfTest()} disabled={busy}>
            {checks ? 'Повторить' : 'Выполнить'}
          </button>
        </div>
      </AdminCard>

      {/* Очередь пробития на ККТ */}
      <AdminCard
        id="equipment.queue"
        title="Очередь пробития на ККТ"
        meta={
          pendingCount > 0 ? (
            <span className="text-small font-normal text-muted">
              ждут: {pendingCount}
            </span>
          ) : undefined
        }
      >
        <p className={hint}>
          Оплачены, но не пробиты на кассе. Ждут отправки: {pendingCount}
        </p>
        {queue.length === 0 ? (
          <p className={okBanner}>Очередь пуста</p>
        ) : (
          <div className={tableWrap}>
            <table className={table}>
              <thead>
                <tr>
                  <th className={th}>Создан</th>
                  <th className={th}>Чек</th>
                  <th className={thNum}>Попытки</th>
                  <th className={th}>Ошибка</th>
                  <th className={th}>Статус / действие</th>
                </tr>
              </thead>
              <tbody>
                {queue.map((item) => {
                  const payload = item.payload as { receiptId?: string; total?: number } | null
                  const errorText = item.lastError ? humanizeFiscalError(item.lastError) : '—'
                  const attemptsDate = item.lastAttemptAt
                    ? new Date(item.lastAttemptAt)
                        .toLocaleString('ru-RU', {
                          day: '2-digit',
                          month: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit'
                        })
                        .replace(',', '')
                    : null
                  return (
                    <tr key={item.id}>
                      <td className={cx(td, 'whitespace-nowrap')}>
                        {new Date(item.createdAt).toLocaleString('ru-RU')}
                      </td>
                      <td className={td}>
                        <code className="font-mono text-tiny">
                          {(payload?.receiptId ?? '').slice(0, 8) || '—'}
                        </code>
                        <div className="text-tiny tabular-nums text-muted">
                          {payload?.total != null ? money(payload.total) : '—'}
                        </div>
                      </td>
                      <td className={cx(tdNum, 'whitespace-nowrap')}>
                        {item.attempts ?? 0}
                        {attemptsDate ? ` · ${attemptsDate}` : ''}
                      </td>
                      <td className={td}>
                        <div
                          className={cx('max-w-[220px] truncate', item.lastError && 'text-danger')}
                          title={errorText}
                        >
                          {errorText}
                        </div>
                      </td>
                      <td className={cx(td, 'whitespace-nowrap')}>
                        <span
                          className={cx(
                            'rounded-md px-2 py-0.5 text-tiny font-semibold',
                            QUEUE_TONE[item.status] ?? 'bg-soft text-muted'
                          )}
                        >
                          {queueStatusLabel(item.status)}
                        </span>
                        {canManage && item.status === 'pending' && (
                          <button
                            className={cx(eqSmallBtn, 'ml-2')}
                            onClick={() => void retryOne(item.id)}
                          >
                            Повторить
                          </button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-2.5 flex gap-2">
          <button
            className={eqBtn}
            onClick={() => void retryAll()}
            disabled={busy || !canManage || pendingCount === 0}
            title={canManage ? 'Отправить все ожидающие чеки' : 'Требуются права'}
          >
            Отправить все
          </button>
        </div>
      </AdminCard>

      {/* Мобильная касса: привязка второго устройства к той же зоне. */}
      <AdminCard
        id="equipment.mobile"
        title="Мобильная касса"
        meta={
          <span className="text-small font-normal text-muted">
            {devicePaired ? 'привязано' : 'не привязано'}
          </span>
        }
      >
        <p className={hint}>
          Смартфон или планшет как вторая касса в той же зоне — общий каталог, смены и
          пул вскрытой тары.
        </p>
        {devicePaired ? (
          <p className="my-1.5 text-small font-semibold text-okDark">
            <Check size={18} /> Привязано: это устройство работает как касса
          </p>
        ) : (
          onPairDevice && (
            <button className={cx(btnBase, btn.primary, btnSize.md)} onClick={onPairDevice}>
              <Phone size={18} /> Привязать телефон
            </button>
          )
        )}
      </AdminCard>

      {/* Весы: статус устройства (порт/протокол/режим) — последний блок раздела. */}
      <AdminCard
        id="equipment.scales"
        title="Весы"
        defaultOpen
        meta={
          <span className="text-small font-normal text-muted">
            {core.scales.getStatus().connected ? 'подключены' : 'нет устройства'}
          </span>
        }
      >
        <ScalesStatusCard core={core} />
      </AdminCard>
    </>
  )
}
